import { Navigate, useSearchParams } from "react-router-dom";
import { useCompanyMode } from "@/hooks/useCompanyMode";
import { useLogisticsRole } from "@/hooks/useLogisticsRole";
import { isSafeModeActive } from "@/lib/audit-mode";

/**
 * Wraps a /dash/* route so audit-mode tenants get bounced to /dash/verify.
 * HomeGate already covers the index route, but sibling routes (e.g. /dash/home,
 * /dash/developers) need their own gate or they leak full Phase 2 chrome.
 *
 * Reads dashboardMode from CompanyModeProvider (loaded by DashV2Layout).
 * ?safe=1 (sticky in sessionStorage) is the rollback escape hatch.
 *
 * Carriers are exempt (2026-07-13, mirrors HomeGate): /dash/verify is the
 * vetting tool for checking OTHERS; carriers are the ones being verified and
 * are RoleGated out of the lookup pages it links to. While the role is still
 * resolving we render nothing rather than guess in either direction.
 */
export function AuditModeRedirect({ children }: { children: React.ReactNode }) {
  const { dashboardMode } = useCompanyMode();
  const { data: logisticsRole, isLoading: roleLoading } = useLogisticsRole();
  const [searchParams] = useSearchParams();
  const safeMode = isSafeModeActive(searchParams);

  if (dashboardMode === "audit" && !safeMode) {
    if (roleLoading) return null;
    if (logisticsRole !== "carrier") {
      return <Navigate to="/dash/verify" replace />;
    }
    // Carriers don't belong on the wrapped sibling routes either (e.g.
    // /dash/home renders DispatcherHomePage) — send them to the index gate,
    // which routes them to CarrierHomePage (red-team M4).
    return <Navigate to="/dash" replace />;
  }

  return <>{children}</>;
}
